import React, { useEffect, useCallback, useState } from 'react';
import { Meeting, meetingService } from '../../services/meetingService';
import { ModalWrapper } from './ModalWrapper';
import { TranscriptPreviewModal } from './TranscriptPreviewModal';
import { POPUP_STYLES } from '../../constants/popupStyles';
import { ContextMenu } from '../ui/ContextMenu';
import { formatDateTime, formatWallClockDateTime } from '../../utils/dateUtils';
import { useTenant } from '../../context/TenantContext';

import DataTableToolbar from '../table/DataTableToolbar';
import DataTableContainer from '../table/DataTableContainer';
import DataTableBody from '../table/DataTableBody';
import ManageColumnsModal from './ManageColumnsModal';
import SaveViewModal from './SaveViewModal';
import useTablePage from '../table/hooks/useTablePage';
import { meetingColumns } from '../../config/meetingColumns';
import { useAuth } from '../../context/AuthContext';
import { Box } from '@mui/material';
import { RunMeetingSummaryDialog } from '../dialogs/RunMeetingSummaryDialog';

export type MeetingAction = 'view' | 'noshow' | 'cancel' | 'finish' | 'comments' | 'email' | 'sms' | 'call' | 'upload-transcript' | 'run-summary';

interface ViewMeetingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: number;
  patientName?: string;
  onAction?: (meeting: Meeting, action: MeetingAction) => void;
  refreshKey?: number;
}

export const ViewMeetingsModal: React.FC<ViewMeetingsModalProps> = ({
  isOpen,
  onClose,
  patientId,
  patientName,
  onAction,
  refreshKey,
}) => {
  const { currentTenant } = useTenant();
  const { user } = useAuth();

  const [previewState, setPreviewState] = useState({
    isOpen: false,
    meetingId: 0,
    textType: 'transcript' as 'transcript' | 'rt_transcript',
    meetingTitle: '',
  });

  const [runSummaryMeeting, setRunSummaryMeeting] = useState<{ id: number; meeting_type_id: number | null } | null>(null);

  const getSortValue = useCallback((meeting: Meeting, key: string): any => {
    switch (key) {
      case 'date': return meeting.scheduled_at || '';
      case 'type': return meeting.meeting_type?.name?.toLowerCase() || '';
      case 'therapist': return meeting.therapist.full_name?.toLowerCase() || '';
      case 'status': return meeting.status.toLowerCase() || '';
      case 'duration': return meeting.duration_minutes || 0;
      case 'title': return meeting.title?.toLowerCase() || '';
      case 'notes': return meeting.notes?.toLowerCase() || '';
      case 'created_at': return meeting.created_at || '';
      case 'updated_at': return meeting.updated_at || '';
      case 'source': return meeting.source?.toLowerCase() || '';
      case 'reason_of_cancelation': return meeting.reason_of_cancelation?.toLowerCase() || '';
      case 'cancellation_note': return meeting.cancellation_note?.toLowerCase() || '';
      case 'cancelation_time': return meeting.cancelation_time || '';
      case 'summary_saved': return meeting.summary_saved ? 1 : 0;
      case 'summary_ready': return meeting.summary_ready ? 1 : 0;
      case 'summary_approved': return meeting.summary_approved ? 1 : 0;
      case 'summary_sent': return meeting.summary_sent ? 1 : 0;
      case 'has_transcript': return meeting.has_transcript ? 1 : 0;
      case 'has_rt_transcript': return meeting.has_rt_transcript ? 1 : 0;
      default: return '';
    }
  }, []);

  const getSearchText = useCallback((meeting: Meeting): string[] => {
    return [
      meeting.meeting_type?.name || '',
      meeting.therapist.full_name || '',
      meeting.status || '',
      meeting.title || '',
      meeting.notes || '',
    ];
  }, []);

  const table = useTablePage<Meeting>({
    entityType: 'meetings',
    columns: meetingColumns,
    tenantId: currentTenant?.id,
    getSortValue,
    getSearchText,
  });

  const fetchMeetings = useCallback(async () => {
    if (!isOpen || !currentTenant || !patientId) return [];
    const data = await meetingService.getMeetings(currentTenant.id, patientId);
    return data;
  }, [isOpen, currentTenant, patientId]);

  useEffect(() => {
    if (isOpen) {
      table.loadData(fetchMeetings, 'Failed to load meetings');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, fetchMeetings, refreshKey]);

  const handleAction = (meeting: Meeting, action: MeetingAction) => {
    table.handleMenuClose();
    onAction?.(meeting, action);
  };

  const handleMakeActive = async (meeting: Meeting) => {
    table.handleMenuClose();
    try {
      await meetingService.updateMeeting(meeting.id, { status: 'active' });
      await table.loadData(fetchMeetings);
    } catch (err) {
      console.error('Failed to mark meeting as active:', err);
    }
  };

  const getContextMenuOptions = (meeting: Meeting) => [
    ...(meeting.status !== 'noshow' ? [{
      label: 'No Show',
      icon: '⛔',
      onClick: () => handleAction(meeting, 'noshow'),
    }] : []),
    ...(meeting.status !== 'cancelled' ? [{
      label: 'Cancel',
      icon: '🚫',
      onClick: () => handleAction(meeting, 'cancel'),
    }] : []),
    ...(meeting.status !== 'active' ? [{
      label: 'Make Active',
      icon: '✅',
      onClick: () => handleMakeActive(meeting),
    }] : []),
    {
      label: 'Finish Treatment',
      icon: (
        <svg fill="#e23232" width="16px" height="16px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M4,1A1,1,0,0,0,3,2V22a1,1,0,0,0,2,0V17H20a1,1,0,0,0,1-1V4a1,1,0,0,0-1-1H5V2A1,1,0,0,0,4,1ZM7,15H5V13H7Zm0-4H5V9H7ZM17,5h2V7H17Zm0,4h2v2H17Zm0,4h2v2H17ZM13,5h2V7H13Zm0,4h2v2H13Zm0,4h2v2H13ZM9,5h2V7H9ZM9,9h2v2H9Zm0,4h2v2H9ZM7,5V7H5V5Z" />
        </svg>
      ),
      onClick: () => handleAction(meeting, 'finish'),
    },
    {
      label: 'Send Email',
      icon: '📧',
      onClick: () => handleAction(meeting, 'email'),
    },
    {
      label: 'Send SMS',
      icon: '📱',
      onClick: () => handleAction(meeting, 'sms'),
    },
    {
      label: 'Call Patient',
      icon: '📞',
      onClick: () => handleAction(meeting, 'call'),
    },
    {
      label: 'Upload Transcript',
      icon: '📤',
      onClick: () => handleAction(meeting, 'upload-transcript'),
    },
    {
      label: 'Edit Comments',
      icon: '💬',
      onClick: () => handleAction(meeting, 'comments'),
    },
    ...(user?.user_privilege?.prompt_development ? [{
      label: 'Run Summary',
      icon: '🤖',
      onClick: () => {
        table.handleMenuClose();
        setRunSummaryMeeting({ id: meeting.id, meeting_type_id: meeting.meeting_type_id });
      },
    }] : []),
  ];

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      active: 'bg-blue-100 text-blue-800',
      done: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      noshow: 'bg-orange-100 text-orange-800',
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  const getColumnValue = (meeting: Meeting, columnKey: string): React.ReactNode => {
    switch (columnKey) {
      case 'date': return <span className="text-[#366A75]">{formatWallClockDateTime(meeting.scheduled_at)}</span>;
      case 'type': return <span className="text-[#366A75]">{meeting.meeting_type?.name || '-'}</span>;
      case 'therapist': return <span className="text-[#366A75]">{meeting.therapist.full_name}</span>;
      case 'status': return getStatusBadge(meeting.status);
      case 'duration': return <span className="text-[#366A75]">{meeting.duration_minutes} min</span>;
      case 'title': return <span className="text-[#366A75]">{meeting.title || '-'}</span>;
      case 'notes': return <span className="text-[#366A75] truncate block max-w-full" title={meeting.notes || ''}>{meeting.notes || '-'}</span>;
      case 'created_at': return <span className="text-[#366A75]">{formatDateTime(meeting.created_at)}</span>;
      case 'updated_at': return <span className="text-[#366A75]">{formatDateTime(meeting.updated_at)}</span>;
      case 'source': return <span className="text-[#366A75]">{meeting.source || '-'}</span>;
      case 'reason_of_cancelation': return <span className="text-[#366A75]">{meeting.reason_of_cancelation || '-'}</span>;
      case 'cancellation_note': return <span className="text-[#366A75] truncate block max-w-full" title={meeting.cancellation_note || ''}>{meeting.cancellation_note || '-'}</span>;
      case 'cancelation_time': return <span className="text-[#366A75]">{meeting.cancelation_time ? formatDateTime(meeting.cancelation_time) : '-'}</span>;
      case 'summary_saved': return <span className={meeting.summary_saved ? 'text-green-600 font-medium' : 'text-gray-400'}>{meeting.summary_saved ? '✓' : '–'}</span>;
      case 'summary_ready': return <span className={meeting.summary_ready ? 'text-green-600 font-medium' : 'text-gray-400'}>{meeting.summary_ready ? '✓' : '–'}</span>;
      case 'summary_approved': return <span className={meeting.summary_approved ? 'text-green-600 font-medium' : 'text-gray-400'}>{meeting.summary_approved ? '✓' : '–'}</span>;
      case 'summary_sent': return <span className={meeting.summary_sent ? 'text-green-600 font-medium' : 'text-gray-400'}>{meeting.summary_sent ? '✓' : '–'}</span>;
      case 'has_transcript': return (
        <span
          className={meeting.has_transcript ? 'text-green-600 font-medium cursor-pointer hover:text-green-800' : 'text-gray-400'}
          onClick={meeting.has_transcript ? (e) => {
            e.stopPropagation();
            setPreviewState({ isOpen: true, meetingId: meeting.id, textType: 'transcript', meetingTitle: meeting.title });
          } : undefined}
        >
          {meeting.has_transcript ? '✓' : '–'}
        </span>
      );
      case 'has_rt_transcript': return (
        <span
          className={meeting.has_rt_transcript ? 'text-green-600 font-medium cursor-pointer hover:text-green-800' : 'text-gray-400'}
          onClick={meeting.has_rt_transcript ? (e) => {
            e.stopPropagation();
            setPreviewState({ isOpen: true, meetingId: meeting.id, textType: 'rt_transcript', meetingTitle: meeting.title });
          } : undefined}
        >
          {meeting.has_rt_transcript ? '✓' : '–'}
        </span>
      );
      default: return '-';
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <ModalWrapper
        isOpen={isOpen}
        onClose={onClose}
        title={`Meetings - ${patientName || 'Patient'}`}
        isLoading={table.loading}
        width="!max-w-6xl"
      >
        <Box sx={{ width: '100%', mb: 2 }}>
          <DataTableToolbar
            {...table.toolbarProps}
            title=""
            searchPlaceholder="Search meetings..."
            isAdmin={!!user?.user_privilege?.config}
            hideSearch={true}
            hideCount={true}
          />
        </Box>

        <div className="overflow-x-auto w-full">
          {table.loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#366A75] mx-auto"></div>
                <p className="text-[#366A75] mt-4 text-sm">Loading meetings...</p>
              </div>
            </div>
          ) : table.error ? (
            <div className="text-center py-12">
              <p className="text-red-600">{table.error}</p>
            </div>
          ) : table.sortedData.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No meetings found</p>
            </div>
          ) : (
            <DataTableContainer {...table.containerProps}>
              <DataTableBody
                data={table.sortedData}
                {...table.tableBodyProps}
                getColumnValue={getColumnValue}
                onRowClick={table.handleRowClick}
                getRowKey={(m) => m.id}
              />
            </DataTableContainer>
          )}
        </div>

        <div className="flex justify-center gap-4 mt-6">
          <button onClick={onClose} className={POPUP_STYLES.button}>
            Close
          </button>
        </div>
      </ModalWrapper>

      {table.selectedItem && table.menuPosition && (
        <ContextMenu
          x={table.menuPosition.left}
          y={table.menuPosition.top}
          options={getContextMenuOptions(table.selectedItem)}
          onClose={table.handleMenuClose}
        />
      )}

      <ManageColumnsModal {...table.manageColumnsProps} />
      <SaveViewModal {...table.saveViewModalProps} isAdmin={!!user?.user_privilege?.config} />

      <TranscriptPreviewModal
        isOpen={previewState.isOpen}
        onClose={() => setPreviewState((s) => ({ ...s, isOpen: false }))}
        meetingId={previewState.meetingId}
        textType={previewState.textType}
        meetingTitle={previewState.meetingTitle}
      />

      {runSummaryMeeting !== null && (
        <RunMeetingSummaryDialog
          open={runSummaryMeeting !== null}
          onClose={() => setRunSummaryMeeting(null)}
          meetingId={runSummaryMeeting.id}
          meetingTypeId={runSummaryMeeting.meeting_type_id}
          zIndex={1500}
        />
      )}
    </>
  );
};

